import React, { FC, useEffect, useState } from 'react'
import useAuthentication from 'hooks/Authentication'
import authenticationActions from 'redux-store/authentication/authentication.actions'
import { getSignupToken } from 'components/session/Signup/Signup'
import {
  SignupFirstStepValues,
  SignupSecondStepValues,
  SignupStep,
  SignupValues
} from 'pages/native/onBoarding/types/types'
import { SignupFirstStep } from '../SignupFirstStep/SignupFirstStep'
import { SignupSecondStep } from '../SignupSecondStep/SignupSecondStep'

import styles from './SignupContent.module.scss'
import { useDispatch } from 'react-redux'
import { useFollowedTeams } from 'pages/native/onBoarding/context/OnBoardingContext'
import { getChannel } from 'api/channel/getChannel'
import ApiEndpoint, { ApiResponse } from 'network/ApiEndpoint'
import SOLIDSPORT_API_PATHS from 'constants/solidsport_api_paths.constans'
import authHeader from 'helpers/authHeader'
import { Company } from 'interfaces'

const signUpInitialValues: SignupValues = {
  firstName: '',
  lastName: '',
  phone: '',
  countryCode: '',
  gender: '',
  birthYear: '',
  email: '',
  confirmEmail: '',
  password: '',
  confirmPassword: '',
  privacyPolicyChecked: false,
  emailConsent: false
}

interface Props {
  currentStep: SignupStep
  setCurrentStep: (step: SignupStep) => void
  onLoginClick(): void
  onAllFormSuccess(): void
}

export async function followChannel(channel: Company) {
  try {
    const response: ApiResponse = await ApiEndpoint.call({
      path: `${SOLIDSPORT_API_PATHS.USERS.FOLLOW.COMPANY}/${channel.subdomain}`,
      method: ApiEndpoint.METHODS.PUT,
      headers: authHeader(),
      authenticatable: true
    })
    if (response.status !== 200) {
      throw Error('unable to follow channel: server error')
    }
  } catch (error: any) {
    console.error(error)
  }
}

export const SignupContent: FC<Props> = ({ currentStep, setCurrentStep }) => {
  const dispatch = useDispatch()
  const { value: followedTeams } = useFollowedTeams()
  const { registered, loggedIn } = useAuthentication()
  const [signupValues, setSignupValues] =
    useState<SignupValues>(signUpInitialValues)

  useEffect(() => {
    dispatch(authenticationActions.clearState())
  }, [])

  const registerNewUser = (userValues: SignupValues) => {
    dispatch(
      authenticationActions.register(
        userValues.firstName,
        userValues.lastName,
        userValues.email,
        userValues.password,
        getSignupToken(),
        userValues.emailConsent,
        userValues.gender,
        userValues.birthYear,
        userValues.countryCode,
        userValues.phone
      )
    )
  }

  const handleFirstStepSubmit = (formValues: SignupFirstStepValues) => {
    setSignupValues({ ...signupValues, ...formValues })
    setCurrentStep(SignupStep.Second)
  }

  const handleSecondStepSubmit = (formValues: SignupSecondStepValues) => {
    const combinedValues = { ...signupValues, ...formValues }
    setSignupValues(combinedValues)
    registerNewUser(combinedValues)
  }

  useEffect(() => {
    if (registered) {
      dispatch(
        authenticationActions.login(signupValues.email, signupValues.password)
      )
      dispatch(authenticationActions.closeLoginLayer())
    }
  }, [registered])

  useEffect(() => {
    const followTeams = async () => {
      if (loggedIn) {
        try {
          for (const team of followedTeams) {
            const channelItem = await getChannel(team.subdomain)
            if (channelItem) {
              await followChannel(channelItem)
            }
          }
        } catch {}
      }
    }

    followTeams()
  }, [loggedIn])

  return (
    <div className={styles.Container}>
      {currentStep === SignupStep.First && (
        <SignupFirstStep
          onSubmitForm={handleFirstStepSubmit}
          initialValues={{
            firstName: signupValues.firstName,
            lastName: signupValues.lastName,
            phone: signupValues.phone,
            countryCode: signupValues.countryCode,
            gender: signupValues.gender,
            birthYear: signupValues.birthYear
          }}
        />
      )}
      {currentStep === SignupStep.Second && (
        <SignupSecondStep
          onSubmitForm={handleSecondStepSubmit}
          initialValues={{
            email: signupValues.email,
            confirmEmail: signupValues.confirmEmail,
            password: signupValues.password,
            confirmPassword: signupValues.confirmPassword,
            privacyPolicyChecked: signupValues.privacyPolicyChecked,
            emailConsent: signupValues.emailConsent
          }}
        />
      )}
    </div>
  )
}
